import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./UserProfileForm.module.scss";
import { FaCamera } from "react-icons/fa";
import { Error, Label } from "@progress/kendo-react-labels";
import { Input } from "@progress/kendo-react-inputs";
import { Loader } from "@progress/kendo-react-indicators";
import { useUploadForm } from "../../../../components/fileUploadHook";
import { FileDocumentTypes } from "../../../../components/Enums/FileDocumentTypes.ts";
import { useSelector } from "react-redux";
import PersonalDetails from "./components/PersonalDetails";
import ApplicationSettings from "./components/ApplicationSettings";
import SecuritySettings from "./components/SecuritySettings";
import defaultProfileImage from "../../../../assets/UserAvatar.svg";
import SubmitButton from "components/common/SubmitButton";
import ConsultantSettings from "./components/ConsultantSettings";
import { useCompany } from "services/companyService";
import { useCurrentUser } from "services/currentUserService";

const getTabs = (isConsultant) => {
  const baseTabs = [
    {
      id: "personal",
      label: "Personal Details",
      component: PersonalDetails,
      required: ["firstName", "emailAddress", "userRef"],
    },
    {
      id: "application",
      label: "Application Settings",
      component: ApplicationSettings,
      required: [],
    },
    {
      id: "security",
      label: "Security Settings",
      component: SecuritySettings,
      required: [],
    },
  ];

  if (isConsultant) {
    baseTabs.push({
      id: "consultant",
      label: "Consultant Settings",
      component: ConsultantSettings,
      required: [],
    });
  }

  return baseTabs;
};

const UserProfileForm = ({
  mode = "add", // 'add' | 'edit' | 'profile'
  initialData,
  onSubmit,
  onCancel,
  onRefreshUser, // Callback to refresh user data from parent
}) => {
  const [activeTab, setActiveTab] = useState("personal");
  const [formData, setFormData] = useState(initialData || {});
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUploading, setProfileImageUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: userData } = useCurrentUser();
  const { data: company } = useCompany();
  const modules = company?.modules || {};
  const isConsultantModuleEnabled = Boolean(modules?.Consultants);
  const TABS = getTabs(isConsultantModuleEnabled);

  const navigate = useNavigate();
  const { uploadForm } = useUploadForm();
  const profilePhotoUrl = initialData?.imageInfo?.documentFile?.downloadURL;

  useEffect(() => {
    if (initialData) {
      setFormData((prevData) => {
        if (prevData && Object.keys(prevData).length > 0) {
          return {
            ...prevData,
            ...initialData,
          };
        }
        return initialData;
      });
      // if (!isRefreshing) {
      //   setHasChanges(false);
      // }
    }
  }, [initialData, isRefreshing]);

  const validateForm = () => {
    const newErrors = {};
    const currentTab = TABS.find((tab) => tab.id === activeTab);

    currentTab.required.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
      }
    });

    if (activeTab === "personal") {
      if (
        formData.emailAddress &&
        !/\S+@\S+\.\S+/.test(formData.emailAddress)
      ) {
        newErrors.emailAddress = "Please enter a valid email address";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleRefreshUser = async () => {
    if (onRefreshUser) {
      setIsRefreshing(true);
      try {
        await onRefreshUser();
        // Mark form as dirty after refresh to enable Update button
        setHasChanges(true);
      } catch (error) {
        console.error("Error refreshing user:", error);
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleImageChange = async (e) => {
    setProfileImageUploading(true);
    const file = e.target.files[0];
    if (file) {
      try {
        setProfileImage(file);
        const dataChoosen = await uploadForm({ filename: file.name }, file, {
          documentType: FileDocumentTypes.UserImage,
          userRef: formData.userRef || "",
        });
        handleChange("userImageFileId", dataChoosen.data?.data?.fileID);
      } catch (error) {
        console.error("Error uploading image:", error);
      } finally {
        setProfileImageUploading(false);
      }
    }
  };

  const handleTabChange = (tabId) => {
    if (validateForm()) {
      setActiveTab(tabId);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitted(true);
    try {
      await onSubmit(formData);
      setHasChanges(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitted(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(-1);
    }
    setHasChanges(false);
  };

  return (
    <div
      style={{ width: "100%", gap: "2rem" }}
      className="d-flex flex-column py-4 px-5"
    >
      <div
        className={`d-flex gap-4 ${styles.container}  ${
          mode === "edit" && "px-3 pt-3"
        }`}
      >
        <div className={styles.sidebar}>
          <div className={styles.modeBadge}>
            {mode === "add" ? "Add User" : "User Settings"}
          </div>
          <div className={styles.profileSection}>
            <div className={styles.profileImage}>
              {profileImageUploading ? (
                <Loader
                  size="medium"
                  type="pulsing"
                  themeColor={"warning"}
                  style={{
                    position: "absolute",
                    top: "45%",
                    left: "40%",
                    zIndex: 100,
                  }}
                />
              ) : (
                <img
                  src={
                    profileImage
                      ? URL.createObjectURL(profileImage)
                      : profilePhotoUrl || defaultProfileImage
                  }
                  alt="Profile"
                  className={
                    !profileImage && !profilePhotoUrl
                      ? ""
                      : ""
                  }
                />
              )}

              <label className={styles.imageUpload}>
                <input
                  type="file"
                  accept="image/*"
                  className={styles.fileInput}
                  onChange={handleImageChange}
                />
                <span className={styles.cameraIconCircle}>
                  <FaCamera size={15} color="white" />
                </span>
              </label>
            </div>

            {mode !== "add" && (
              <div className={styles.UserRef}>
                <Label>User Ref</Label>
                <Input
                  name="userRef"
                  value={formData.userRef || ""}
                  onChange={(e) => handleChange("userRef", e.value)}
                  placeholder="Enter user reference"
                  className={`mt-1 ps-3 fw-bold text-black ${
                    errors.userRef ? "k-invalid" : ""
                  }`}
                  disabled={mode !== "add"}
                />
                {errors.userRef && <Error>{errors.userRef}</Error>}
              </div>
            )}

            <div className={styles.tabList}>
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`${styles.tabButton} ${
                    activeTab === tab.id ? styles.active : ""
                  }`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.formSection}>
            {TABS.map(
              (tab) =>
                activeTab === tab.id && (
                  <tab.component
                    key={tab.id}
                    data={formData}
                    onChange={handleChange}
                    mode={mode}
                    errors={errors}
                    onRefreshUser={
                      tab.id === "application" ? handleRefreshUser : undefined
                    }
                  />
                ),
            )}
          </div>
        </div>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className="bgBtnSecondary common-btn me-2"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <SubmitButton
          onClick={handleSubmit}
          isLoading={isSubmitted}
          disabled={mode !== "add" && !hasChanges}
        >
          {mode === "add" ? "Save" : "Update"}
        </SubmitButton>
      </div>
    </div>
  );
};

export default UserProfileForm;
